<template>
  <section class="full-screen" :class="{'px-6': isDesktopView}">
    <header class="dashboard-header mt-6 mb-5 px-4">
      <div class="user-profile">
        <div class="welcome-text">
          <p
            class="is-size-7 has-text-grey is-uppercase has-text-weight-bold letter-spacing-1"
          >
            Dashboard
          </p>
          <h1 class="title is-4 is-blue">
            Hello, {{ user?.displayName?.split(" ")[0] }}! 👋
          </h1>
        </div>
        <div class="status-pill" v-if="user?.role === ResellerRoles.driver">
          <span
            class="pulse-dot"
            :class="{ 'is-active': vendorData?.tripStarted }"
          ></span>
          {{ vendorData?.tripStarted ? "On Duty" : "Off Duty" }}
        </div>
      </div>
    </header>

    <section class="container-bg px-4">
      <section v-if="user?.role === ResellerRoles.driver">
        <b-modal v-model="showSetupHelper" full-screen>
          <SetupNotificationsHelper
            @toggleModal="toggleModal"
            :driver-id="user?.id"
          />
        </b-modal>

        <div
          class="notification-prompt-card mb-5"
          @click="showSetupHelper = true"
        >
          <div class="content">
            <div class="text-side">
              <span class="has-text-weight-bold mb-2"
                >Enable Notifications</span
              >
              <p class="is-size-7">Don't miss new ride requests</p>
            </div>
            <b-icon pack="fas" icon="bell" class="bell-icon"></b-icon>
          </div>
        </div>

        <div class="hero-trip-card mb-5">
          <div
            class="card-content-wrapper"
            :class="{ 'active-gradient': vendorData?.tripStarted }"
          >
            <div class="trip-main-info">
              <div class="icon-blob">
                <b-icon
                  pack="fas"
                  :icon="vendorData?.tripStarted ? 'couch' : 'bus-alt'"
                  size="is-medium"
                ></b-icon>
              </div>
              <div class="info-content">
                <p class="has-text-weight-bold is-size-5">
                  {{
                    vendorData?.tripStarted
                      ? "Trip in Progress"
                      : "No Active Trip"
                  }}
                </p>
                <p class="is-size-6 opacity-8">
                  {{
                    vendorData?.tripStarted
                      ? vendorData.availableSeats + " seats available"
                      : "Ready to start your shift?"
                  }}
                </p>
              </div>
            </div>
            <router-link
              :to="{ name: 'trip-management' }"
              class="hero-action-btn"
            >
              {{ vendorData?.tripStarted ? "Manage Trip" : "Start New Trip" }}
              <b-icon
                pack="fas"
                icon="arrow-right"
                size="is-small"
                class="ml-2"
              ></b-icon>
            </router-link>
          </div>
        </div>

        <div class="schedule-summary-wrapper mb-5">
          <TicketsScheduledSummary />
        </div>

        <div class="quick-actions-grid mb-5">
          <router-link
            :to="{ name: 'generate-ticket' }"
            class="action-card dark-card"
          >
            <div class="action-icon">
              <b-icon pack="fas" icon="plus"></b-icon>
            </div>
            <div>
              <p class="has-text-weight-bold">Sell Ticket</p>
              <p class="is-size-7 opacity-7">New booking</p>
            </div>
          </router-link>

          <router-link
            v-if="isMobileView"
            :to="{ name: 'driver-sales' }"
            class="action-card green-card"
          >
            <div class="action-icon">
              <b-icon pack="fas" icon="chart-line"></b-icon>
            </div>
            <div>
              <p class="has-text-weight-bold">Sales Report</p>
              <p class="is-size-7 opacity-7">History</p>
            </div>
          </router-link>
        </div>

       <!--  <div class="stats-row mb-6">
          <div class="stat-item">
            <p class="label">Tickets Sold</p>
            <p class="value">{{ ticketsSold }}</p>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <p class="label">Earnings</p>
            <p class="value">{{ parseFloat(commissionEarned.toFixed(2)) }}€</p>
          </div>
        </div> -->
      </section>

      <div v-if="shouldShowReferralLink">
        <b-field label="Your referral link" class="mb-5 mt-5">
          <div class="disabled-input">
            <span class="text">{{ referralLink }}</span>
          </div>
          <div @click="copyTextToClipboard(referralLink)">
            <b-icon pack="far" class="icon-container" icon="copy"></b-icon>
          </div>
        </b-field>

        <div
        v-if="isMobileView"
          class="blue-button mb-5"
          @click="showQrCodeModal = !showQrCodeModal"
        >
          <b-icon icon="qrcode"></b-icon>
          Show Qr Code
        </div>
        <ReferralQrModal
          :active.sync="showQrCodeModal"
          :referralCode="referralLink"
        />

        <div
          class="columns is-mobile is-multiline is-variable is-2 mt-3 mb-5 stats-wrapper"
        >
          <div class="column is-4-tablet is-6-mobile">
            <div class="info-container blue h-100">
              <div class="info-tag">
                <b-icon pack="fas" icon="chart-bar" class="mb-2"></b-icon>
                <span>Tickets sold</span>
              </div>
              <p class="info-text">{{ ticketsSold }}</p>
            </div>
          </div>

          <div class="column is-4-tablet is-6-mobile">
            <div class="info-container green h-100">
              <div class="info-tag">
                <b-icon pack="fas" icon="wallet" class="mb-2"></b-icon>
                <span>Earnings</span>
              </div>
              <p class="info-text">
                {{ parseFloat(commissionEarned.toFixed(2)) }}€
              </p>
            </div>
          </div>

          <div class="column is-4-tablet is-6-mobile">
            <div class="info-container orange h-100">
              <div class="info-tag">
                <b-icon pack="fas" icon="clock" class="mb-2"></b-icon>
                <span>Pending</span>
              </div>
              <p class="info-text">
                {{ parseFloat(outstandingPayments.toFixed(2)) }}€
              </p>
            </div>
          </div>

          <div
            class="column is-4-tablet is-6-mobile"
            v-if="shouldShowAmountOwed"
          >
            <div class="info-container red h-100">
              <div class="info-tag">
                <b-icon pack="fas" icon="comments-dollar" class="mb-2"></b-icon>
                <span>Owe</span>
              </div>
              <p class="info-text">
                {{ parseFloat(amountOwned.toFixed(2)) }}€
              </p>
            </div>
          </div>
        </div>
      </div>

      <CommisionsTable
        v-if="user?.role !== ResellerRoles.driver"
        :class="
          user?.role === ResellerRoles.hotel
            ? 'mt-6 mb-6 pb-6'
            : 'mt-4 mb-6 pb-6'
        "
        :activities="activities"
        :sales="salesHistory"
        :role="user?.role"
      />

      <DriverSalesTable
        v-if="user?.role === ResellerRoles.driver && !isMobileView"
        class="mb-6 pb-6"
        :vendor="user"
        is-driver-view
      />
    </section>
  </section>
</template>

<script>
import { ResellerRoles } from "../enums/ResellerRoles";
import { mapState, mapMutations } from "vuex";
import {
  UsersStates,
  ActivitiesStates,
  ActivitiesMutations,
} from "../store/Storetypes";
import { db } from "../firebase";
import { HelpersMixin } from "../mixins";
import CommisionsTable from "./CommisionsTable.vue";
import DriverSalesTable from "../Admin/DriverSalesTable.vue";
import TicketsScheduledSummary from "../Admin/components/TicketsScheduledSummary.vue";
import SetupNotificationsHelper from "./components/SetupNotificationsHelper.vue";
import ReferralQrModal from "../components/ReferralQrModal.vue";

export default {
  mixins: [HelpersMixin],
  components: {
    CommisionsTable,
    DriverSalesTable,
    TicketsScheduledSummary,
    SetupNotificationsHelper,
    ReferralQrModal,
  },
  data() {
    return {
      isLoading: false,
      ResellerRoles,
      ticketsSold: 0,
      commissionEarned: 0,
      totalPayouts: 0,
      outstandingPayments: 0,
      amountOwned: 0.0,
      salesHistory: [],
      vendorData: null,
      showSetupHelper: false,
      unsubscribe: null,
      showQrCodeModal: false,
    };
  },
  computed: {
    ...mapState({
      activities: (state) => state.activities[ActivitiesStates.ACTIVITIES],
      user: (state) => state.users[UsersStates.USER],
      isVendor: (state) => state.users[UsersStates.IS_VENDOR],
    }),
    referralLink() {
      return `https://guiders.pt?referral=${this.user.id}`;
    },
    shouldShowReferralLink() {
      console.log(
        this.user?.role === ResellerRoles.hotel ||
          this.user?.role === ResellerRoles.externalSeller,
      );

      return (
        this.user?.role === ResellerRoles.hotel ||
        this.user?.role === ResellerRoles.externalSeller
      );
    },
    shouldShowAmountOwed() {
      return (
        this.role === ResellerRoles.hotel ||
        this.role === ResellerRoles.internalSeller
      );
    },
  },
  created() {
    this.getActivityData();

    this.unsubscribe = db
      .firestore()
      .collection("vendors")
      .doc(this.user.id)
      .onSnapshot(
        (doc) => {
          if (!doc.exists) return;
          this.vendorData = { id: doc.id, ...doc.data() };
          this.ticketsSold = 0;
          this.commissionEarned = 0.0;
          this.totalPayouts = 0.0;
          this.outstandingPayments = 0.0;
          this.amountOwned = 0.0;

          this.salesHistory = this.vendorData.salesHistory || [];

          if (!this.salesHistory.length) return;

          this.salesHistory.forEach((sale) => {
            this.ticketsSold += Number(sale.numberOfTickets) || 0;
            if (sale?.commission) {
              this.commissionEarned += sale.commission;
              if (sale.paid) {
                this.totalPayouts += sale.commission;
              } else {
                this.outstandingPayments += sale.commission;
              }
            }
            if (sale?.saleSource === "Front Desk Sale") {
              this.amountOwned +=
                parseFloat(sale.totalPrice || 0) -
                parseFloat(sale.commission || 0);
            }
          });
        },
        (error) => {
          console.error("Error listening to vendor data:", error);
        },
      );
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    ...mapMutations({
      getActivityData: ActivitiesMutations.GET_ACTIVITY_DATA,
    }),
    copyTextToClipboard(text) {
      if (!text) return;
      this.copyToClipboard(text);
    },
    toggleModal(isActive) {
      this.showSetupHelper = isActive;
    },
  },
};
</script>

<style lang="scss" scoped>
/* MODERN DASHBOARD THEME */
$primary-dark: #2c2c2c;
$emerald: #2fc2a9;
$soft-blue: #3b74ee;
$bg-light: #f9fbff;

.dashboard-header {
  .user-profile {
    justify-content: space-between;
    align-items: center;
  }
}

.app-page {
  padding: 0;
}

.stats-container {
  display: flex;
  flex-wrap: wrap;
  margin-left: -0.5rem; // Adjusting Bulma column gutters
  margin-right: -0.5rem;
}

.info-container-wrapper {
  display: flex; /* Makes the column a flex container */
  padding: 0.5rem; /* Equal spacing between cards */
}

/* NOTIFICATION PROMPT */
.notification-prompt-card {
  background: #2c3e50;
  color: white;
  padding: 16px 20px;
  border-radius: 16px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(59, 116, 238, 0.2);

  .content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .bell-icon {
    animation: ring 4s infinite;
  }
}

.notification-settings-row {
  background: white;
  padding: 16px;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.2s;

  &:active {
    background: #f8fafc;
  }
}

@keyframes ring {
  0%,
  100% {
    transform: rotate(0);
  }
  5% {
    transform: rotate(15deg);
  }
  10% {
    transform: rotate(-15deg);
  }
  15% {
    transform: rotate(10deg);
  }
  20% {
    transform: rotate(-10deg);
  }
  25% {
    transform: rotate(0);
  }
}

.status-pill {
  background: white;
  padding: 8px 14px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  margin-top: 1rem;

  .pulse-dot {
    height: 10px;
    width: 10px;
    background-color: #cbd5e1;
    border-radius: 50%;
    &.is-active {
      background-color: $emerald;
      animation: pulse 2s infinite;
    }
  }
}

/* HERO TRIP CARD */
.hero-trip-card {
  .card-content-wrapper {
    background: white;
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 20px;
    border: 1px solid rgba(0, 0, 0, 0.02);

    &.active-gradient {
      background: #2fc2a9;
      color: white;
      .icon-blob {
        background: rgba(255, 255, 255, 0.2);
        color: white;
      }
      .hero-action-btn {
        background: white;
        color: #015840;
      }
    }
  }

  .trip-main-info {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .icon-blob {
    padding: 14px;
    background: #f0fdf4;
    border-radius: 16px;
    color: $emerald;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .hero-action-btn {
    background: $primary-dark;
    color: white;
    padding: 14px;
    border-radius: 12px;
    text-align: center;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s;
    &:active {
      transform: scale(0.98);
    }
  }
}

/* QUICK ACTIONS */
.quick-actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  .action-card {
    padding: 20px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: white;

    &.dark-card {
      background: #2c2c2c;
    }
    &.green-card {
      background: #48c78e;
    }

    .action-icon {
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

/* STATS ROW */
.stats-row {
  background: white;
  padding: 24px;
  border-radius: 20px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);

  .stat-item {
    text-align: center;
    .label {
      font-size: 0.7rem;
      color: #94a3b8;
      font-weight: 700;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .value {
      font-size: 1.5rem;
      font-weight: 800;
      color: #1e293b;
    }
  }

  .stat-divider {
    width: 1px;
    height: 35px;
    background: #f1f5f9;
  }
}

/* Wrapper ensures the columns behave as a flex container */
.stats-wrapper {
  display: flex;
  align-items: stretch; /* This forces all children to have the same height */
}

.info-container {
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  background: white;
  height: 100%; /* Fill the column height */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /* REMOVE max-width: calc(50% - 1rem); <--- This was the culprit */

  .info-tag {
    display: flex;
    flex-direction: column;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 1rem;

    /* Styling for the icons within tags */
    .icon {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }

    &.blue {
      color: #3b74ee;
      .icon {
        background: #dbeafe;
      }
    }
    &.green {
      color: #2ba880;
      .icon {
        background: #d1fae5;
      }
    }
    &.orange {
      color: #d97706;
      .icon {
        background: #fef3c7;
      }
    }
    &.red {
      color: #fe5f55;
      .icon {
        background: #ffebeb;
      }
    }
  }

  .info-text {
    font-size: 1.6rem;
    font-weight: 700;
    color: #1e293b;
    margin-top: auto; /* Keeps numbers at the bottom */
  }
}

/* Utility to ensure full height on the inner card */
.h-100 {
  height: 100% !important;
}

.info-container {
  flex: 1; /* Makes card fill the full height of the row */
  display: flex;
  flex-direction: column;
  justify-content: space-between; /* Pushes value to bottom if title is long */
  padding: 1.25rem;
  border-radius: 16px; /* Smoother corners like your image */
  border: 1px solid #f1f5f9;
  background: white;
  transition: transform 0.2s ease;

  .info-tag {
    display: flex;
    flex-direction: column;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;

    .icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;
    }
  }

  /* Colors and Icon Backgrounds */
  &.blue {
    .info-tag {
      color: #3b74ee;
    }
    .icon {
      background: #dbeafe;
    }
  }
  &.green {
    .info-tag {
      color: #10b981;
    }
    .icon {
      background: #d1fae5;
    }
  }
  &.orange {
    .info-tag {
      color: #f05a45;
    }
    .icon {
      background: #fef3c7;
    }
  }

  .info-text {
    font-size: 1.6rem;
    font-weight: 800;
    color: #1e293b;
    margin-top: auto; /* Ensures text stays at the bottom if content varies */
  }
}

.disabled-input {
  cursor: not-allowed;
  width: 100%;
  border: 1px solid #eee;
  background-color: #eeeeee57;
  color: #4b5563;
  border-radius: 8px;
  height: 3rem;
  display: flex;
  align-items: center;
  padding: 0 12px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.icon-container {
  border-radius: 8px;
  border: 1px solid #eee;
  height: 3rem;
  width: 3rem;
  color: #5584F1;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(47, 194, 169, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(47, 194, 169, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(47, 194, 169, 0);
  }
}

.letter-spacing-1 {
  letter-spacing: 1.5px;
}
.opacity-8 {
  opacity: 0.8;
}
.opacity-7 {
  opacity: 0.7;
}
</style>
